import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Stack,
    Chip,
    Divider,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    LocationOn,
    Phone,
    AccessTime,
    LocalShipping,
    Business,
    Edit,
    Close,
    Map
} from '@mui/icons-material';
import { getCustomerById } from '../services/customerService';
import DropLotManagement from './DropLotManagement';
import CustomerForm from './CustomerForm';

function CustomerDetailView({ open, onClose, customerId, customer: propCustomer }) {
    const [customer, setCustomer] = useState(propCustomer || null);
    const [loading, setLoading] = useState(!propCustomer);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [editFormOpen, setEditFormOpen] = useState(false);

    useEffect(() => {
        if (open && customerId && !propCustomer) {
            loadCustomer();
        } else if (propCustomer) {
            setCustomer(propCustomer);
            setLoading(false);
        }
    }, [open, customerId, propCustomer]);

    const loadCustomer = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getCustomerById(customerId);
            setCustomer(response.data);
        } catch (err) {
            console.error('Error loading customer:', err);
            setError('Failed to load customer details');
        } finally {
            setLoading(false);
        }
    };

    const formatAddress = (address) => {
        if (!address) return 'No address provided';
        const parts = [address.street, address.city, address.state, address.zip].filter(Boolean);
        return parts.join(', ');
    };

    const handleEditSave = () => {
        setEditFormOpen(false);
        loadCustomer(); // Refresh customer data
    };

    const TabPanel = ({ children, value, index }) => (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Business color="primary" />
                        <Typography variant="h6">
                            {customer?.name || 'Customer Details'}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }} data-usage-screen="customers.detail">
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {customer && !loading && !error && (
                    <Box>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Customer Details" />
                            <Tab label="Drop Lot Locations" />
                        </Tabs>

                        <TabPanel value={activeTab} index={0}>
                            <Box sx={{ px: 3 }}>
                                {/* Customer Information Card */}
                                <Card sx={{ mb: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                            <Typography variant="h5" component="div">
                                                {customer.name}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                startIcon={<Edit />}
                                                onClick={() => setEditFormOpen(true)}
                                            >
                                                Edit
                                            </Button>
                                        </Stack>

                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                                                
                                                <Stack spacing={2}>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Business fontSize="small" />
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Contact Person</Typography>
                                                            <Typography variant="body1">{customer.contactPerson || 'N/A'}</Typography>
                                                        </Box>
                                                    </Stack>

                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Phone fontSize="small" />
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                                            <Typography variant="body1">{customer.contactPhone || 'N/A'}</Typography>
                                                        </Box>
                                                    </Stack>


                                                </Stack>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="h6" gutterBottom>Location & Address</Typography>
                                                
                                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                                    <LocationOn fontSize="small" sx={{ mt: 0.5 }} />
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">Address</Typography>
                                                        <Typography variant="body1">{formatAddress(customer.address)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                                <Typography variant="h6" gutterBottom>Operational Details</Typography>
                                                
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <AccessTime fontSize="small" />
                                                            <Box>
                                                                <Typography variant="body2" color="text.secondary">Operating Hours</Typography>
                                                                <Typography variant="body1">{customer.openHours || 'N/A'}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Avg Unload Time</Typography>
                                                            <Typography variant="body1">
                                                                {customer.avgUnloadTimeMinutes ? `${customer.avgUnloadTimeMinutes} minutes` : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Overnight Parking</Typography>
                                                            <Chip
                                                                label={customer.overnightParking ? "Allowed" : "Not Allowed"}
                                                                color={customer.overnightParking ? "success" : "default"}
                                                                size="small"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Lumper Fee</Typography>
                                                            <Chip
                                                                label={customer.lumperFee ? "Required" : "Not Required"}
                                                                color={customer.lumperFee ? "warning" : "default"}
                                                                size="small"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {(customer.detentionPolicy || customer.notes) && (
                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant="h6" gutterBottom>Additional Information</Typography>
                                                    
                                                    {customer.detentionPolicy && (
                                                        <Box sx={{ mb: 2 }}>
                                                            <Typography variant="body2" color="text.secondary">Detention Policy</Typography>
                                                            <Typography variant="body1">{customer.detentionPolicy}</Typography>
                                                        </Box>
                                                    )}

                                                    {customer.notes && (
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Notes</Typography>
                                                            <Typography variant="body1">{customer.notes}</Typography>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <Box sx={{ px: 3 }}>
                                <DropLotManagement customer={customer} />
                            </Box>
                        </TabPanel>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            {/* Edit Customer Form */}
            <CustomerForm
                open={editFormOpen}
                onClose={() => setEditFormOpen(false)}
                customer={customer}
                onSave={handleEditSave}
            />
        </Dialog>
    );
}

export default CustomerDetailView; 